import { useEffect, useState } from "react"
import { Button, Form } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import { isEmail } from "validator"

import { useFetchUsersQuery } from "../../../app/adminApiSlice"
import { useFetchClubsQuery } from "../../../app/clubsApiSlice"
import { useCreateClubMutation } from "../../../app/clubsApiSlice"
import ControlInput from "../../../components/form-inputs/ControlInput"
import {
  isValidAddress,
  isValidPostcode,
  isValidCountry,
  isValidCityOrCounty,
  isValidMembershipSpaces
} from "../../../utils/validateInput"
import LoadingTextSwap from "../../../components/LoadingTextSwap"
import BackButtonHeader from "../../../components/buttons/BackButtonHeader"
import findUser from "../../../utils/findUser"

const CreateClubPage = () => {
  const navigate = useNavigate()
  const { data: users } = useFetchUsersQuery()
  const { data: clubs } = useFetchClubsQuery()
  const [createClub, { isLoading }] = useCreateClubMutation()

  const [clubName, setClubName] = useState("")
  const [streetAddress, setStreetAddress] = useState("")
  const [city, setCity] = useState("")
  const [county, setCounty] = useState("")
  const [postcode, setPostcode] = useState("")
  const [country, setCountry] = useState("")
  const [secretaryEmail, setSecretaryEmail] = useState("")
  const [headCoachEmail, setHeadCoachEmail] = useState("")
  const [welfareOfficerEmail, setWelfareOfficerEmail] = useState("")
  const [dbsVerifierEmail, setDbsVerifierEmail] = useState("")
  const [membershipSpaces, setMembershipSpaces] = useState(0)

  const [valid, setValid] = useState({
    clubName: false,
    streetAddress: false,
    city: false,
    county: false,
    postcode: false,
    country: false,
    secretaryEmail: false,
    headCoachEmail: false,
    welfareOfficerEmail: false,
    dbsVerifierEmail: false,
    membershipSpaces: true
  })

  const [blurAll, setBlurAll] = useState(false)
  const [error, setError] = useState("")

  const [secretaryEmailError, setSecretaryEmailError] = useState("")
  const [headCoachEmailError, setHeadCoachEmailError] = useState("")
  const [welfareOfficerEmailError, setWelfareOfficerEmailError] = useState("")
  const [dbsVerifierEmailError, setDbsVerifierEmailError] = useState("")
  const [clubNameError, setClubNameError] = useState("")

  const emailErrorFunctionsMap = {
    secretary: setSecretaryEmailError,
    headCoach: setHeadCoachEmailError,
    welfareOfficer: setWelfareOfficerEmailError,
    dbsVerifier: setDbsVerifierEmailError,
    clubName: setClubNameError
  }

  const allValid = Object.values(valid).every(Boolean)
  const hasEmailErrors = [secretaryEmailError, headCoachEmailError, welfareOfficerEmailError, dbsVerifierEmailError, clubNameError].some(Boolean)

  // Clear error message when all fields are valid
  useEffect(() => {
    if (error && allValid && !hasEmailErrors) {
      setError("")
    }
  }, [allValid, hasEmailErrors])

  // Function to update valid state
  const updateValid = (key, value) => {
    setValid(prev => ({ ...prev, [key]: value }))
  }

  const secretaryChange = (val) => {
    setSecretaryEmail(val)

    if (secretaryEmailError) {
      setSecretaryEmailError("")
    }
  }

  const headCoachChange = (val) => {
    setHeadCoachEmail(val)

    if (headCoachEmailError) {
      setHeadCoachEmailError("")
    }
  }

  const welfareOfficerChange = (val) => {
    setWelfareOfficerEmail(val)

    if (welfareOfficerEmailError) {
      setWelfareOfficerEmailError("")
    }
  }

  const dbsVerifierChange = (val) => {
    setDbsVerifierEmail(val)

    if (dbsVerifierEmailError) {
      setDbsVerifierEmailError("")
    }
  }

  const handleNameChange = (val) => {
    setClubName(val)

    if (clubNameError) {
      setClubNameError("")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (error || isLoading) return

    setBlurAll(true)

    if (!allValid) {
      return setError("All fields must be valid")
    }

    let inputError = false

    // Check if club name is already in use
    const trimmedClubName = clubName.trim().toLowerCase()

    for (const club in clubs) {
      if (!clubs[club]?.clubName) continue
      if (clubs[club].clubName.toLowerCase() === trimmedClubName) {
        inputError = true;
        setClubNameError("Club name already in use")
        break
      }
    }

    // Check all emails and get user ids
    // Define the roles and their corresponding setters
    const roleChecks = [
      {
        email: secretaryEmail,
        setError: setSecretaryEmailError,
        role: 'secretary',
        title: 'Secretary'
      },
      {
        email: headCoachEmail,
        setError: setHeadCoachEmailError,
        role: 'headCoach',
        title: 'Head Coach'
      },
      {
        email: welfareOfficerEmail,
        setError: setWelfareOfficerEmailError,
        role: 'welfareOfficer',
        title: 'Welfare Officer'
      },
      {
        email: dbsVerifierEmail,
        setError: setDbsVerifierEmailError,
        role: 'dbsVerifier',
        title: 'DBS Verifier'
      }
    ]

    // Check all emails and validate
    const userIds = {}

    for (const { email, setError, role, title } of roleChecks) {
      const result = findUser(email, users) || {}
      const { userId, memberDetailsComplete } = result

      if (!userId) {
        inputError = true
        setError(`No member found with email '${email}'`)
      } else if (!memberDetailsComplete) {
        inputError = true
        setError(`${title} must complete their member profile before being assigned this role`)
      } else {
        userIds[`${role}Id`] = userId
      }
    }

    // Destructure to get individual IDs for the next part
    const {
      secretaryId,
      headCoachId,
      welfareOfficerId,
      dbsVerifierId
    } = userIds

    if (inputError) return setError("All fields must be valid")

    try {
      await createClub({
        clubName: trimmedClubName,
        address: {
          streetAddress: streetAddress.trim(),
          city: city.trim(),
          county: county.trim(),
          postcode: postcode.trim(),
          country: country.trim()
        },
        secretaryId,
        headCoachId,
        welfareOfficerId,
        dbsVerifierId,
        membershipSpaces: Number(membershipSpaces) || 0
      }).unwrap()

      toast.success("Club created")

      navigate("/admin/clubs")

    } catch (error) {
      console.log(error)

      if (error?.status === "functions/failed-precondition" && typeof error.data?.errors === "object") {
        Object.entries(error.data.errors).forEach(([key, value]) => {
          emailErrorFunctionsMap[key](value)
        })
      } else {
        toast.error("Error creating new club")

        navigate("/admin/clubs")
      }
    }
  }

  return (
    <div className="w-100">
      <BackButtonHeader page="Create club" />

      <Form
        className="w-100 mx-auto bg-white shadow-sm rounded-4 p-4" onSubmit={handleSubmit}>
        <div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Club Name</b>
            <div className="w-100">
              <ControlInput
                type="text"
                name="clubName"
                value={clubName}
                isValid={valid.clubName && !clubNameError}
                setValid={updateValid}
                setValue={handleNameChange}
                validTest={(value) => value.trim() !== ""}
                blurAll={blurAll}
                feedback={clubNameError || "Please enter a valid club name"}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row  border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Club Address</b>
            <div className="w-100 d-flex flex-column gap-2">
              <ControlInput
                type="text"
                name="streetAddress"
                value={streetAddress}
                isValid={valid.streetAddress}
                setValid={updateValid}
                setValue={setStreetAddress}
                validTest={isValidAddress}
                blurAll={blurAll}
                feedback="Please enter a valid street address"
                placeholder="Street Address"
              />
              <ControlInput
                type="text"
                name="city"
                value={city}
                isValid={valid.city}
                setValid={updateValid}
                setValue={setCity}
                validTest={isValidCityOrCounty}
                blurAll={blurAll}
                feedback="Please enter a valid town or city"
                placeholder="Town or City"
              />
              <ControlInput
                type="text"
                name="county"
                value={county}
                isValid={valid.county}
                setValid={updateValid}
                setValue={setCounty}
                validTest={isValidCityOrCounty}
                blurAll={blurAll}
                feedback="Please enter a valid county or state"
                placeholder="County or State"
              />
              <ControlInput
                type="text"
                name="postcode"
                value={postcode}
                isValid={valid.postcode}
                setValid={updateValid}
                setValue={setPostcode}
                validTest={isValidPostcode}
                blurAll={blurAll}
                feedback="Please enter a valid postcode"
                placeholder="Postcode"
              />
              <ControlInput
                type="text"
                name="country"
                value={country}
                isValid={valid.country}
                setValid={updateValid}
                setValue={setCountry}
                validTest={isValidCountry}
                blurAll={blurAll}
                feedback="Please enter a valid country"
                placeholder="Country"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Secretary Email</b>
            <div className="w-100">
              <ControlInput
                type="email"
                name="secretaryEmail"
                value={secretaryEmail}
                isValid={valid.secretaryEmail && !secretaryEmailError}
                setValid={updateValid}
                setValue={secretaryChange}
                validTest={isEmail}
                blurAll={blurAll}
                feedback={secretaryEmailError || "Please enter a valid email address"}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Head Coach Email</b>
            <div className="w-100">
              <ControlInput
                type="email"
                name="headCoachEmail"
                value={headCoachEmail}
                isValid={valid.headCoachEmail && !headCoachEmailError}
                setValid={updateValid}
                setValue={headCoachChange}
                validTest={isEmail}
                blurAll={blurAll}
                feedback={headCoachEmailError || "Please enter a valid email address"}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Welfare Officer Email</b>
            <div className="w-100">
              <ControlInput
                type="email"
                name="welfareOfficerEmail"
                value={welfareOfficerEmail}
                isValid={valid.welfareOfficerEmail && !welfareOfficerEmailError}
                setValid={updateValid}
                setValue={welfareOfficerChange}
                validTest={isEmail}
                blurAll={blurAll}
                feedback={welfareOfficerEmailError || "Please enter a valid email address"}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">DBS Verifier Email</b>
            <div className="w-100">
              <ControlInput
                type="email"
                name="dbsVerifierEmail"
                value={dbsVerifierEmail}
                isValid={valid.dbsVerifierEmail && !dbsVerifierEmailError}
                setValid={updateValid}
                setValue={dbsVerifierChange}
                validTest={isEmail}
                blurAll={blurAll}
                feedback={dbsVerifierEmailError || "Please enter a valid email address"}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Member Spaces (yearly)</b>
            <div className="w-100">
              <ControlInput
                type="number"
                name="membershipSpaces"
                value={membershipSpaces}
                isValid={valid.membershipSpaces}
                setValid={updateValid}
                setValue={setMembershipSpaces}
                validTest={isValidMembershipSpaces}
                blurAll={blurAll}
                feedback="Please enter a valid number"
              />
            </div>
          </div>
        </div>
        {(error) && <p className="text-danger i text-center fw-semibold mt-3">{error}</p>}
        <Button
          disabled={isLoading}
          className="px-4 mt-2 mx-auto d-block rounded-3"
          variant="dark"
          type="submit"
        >
          <LoadingTextSwap isLoading={isLoading}>
            Create Club
          </LoadingTextSwap>
        </Button>
      </Form>
    </div>
  )
}

export default CreateClubPage